import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/hooks/use-role";

// A-08: server-side role verification on mount.
// Calls get-user-role Edge Function (reads from DB, not client cache) to confirm
// the user still has admin access. Protects against stale cache after role revocation.
function useServerRoleCheck() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["server-role-check-admin"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-user-role");
      if (error) throw error;
      const serverRole: string = data?.role || "user";
      const hasAccess = serverRole === "admin" || serverRole === "superadmin";
      if (!hasAccess) {
        // Invalidate cached role so useRole() reflects reality on next render
        queryClient.invalidateQueries({ queryKey: ["user-role"] });
      }
      return { serverRole, hasAccess };
    },
    // Always fetch fresh on mount; never serve a stale server-check result
    staleTime: 0,
    retry: 1,
  });
}

export default function AdminRoute({ children }: { children: React.ReactNode }) {
  const { hasAdminAccess, loading: roleLoading } = useRole();
  const { data: serverCheck, isLoading: serverLoading } = useServerRoleCheck();

  if (roleLoading || serverLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Deny if either client cache or server check says no access
  if (!hasAdminAccess || serverCheck?.hasAccess === false) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
